import { useState } from 'react'
import { startLogin, verifyLogin } from '../lib/auth'
import NotificationModal from './ui/notification-modal'
import { SupportEmail } from './legal/legal-page'
import memoarLogo from '../assets/memoar-logo.png'

interface LoginFormProps {
  onSuccess: (user: { id: string; email: string }) => void
}

export default function LoginForm({ onSuccess }: LoginFormProps): JSX.Element {
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      await startLogin(email)
      // Always advance, even for an unknown address: the endpoint returns 200
      // either way so that this form cannot reveal who has an account.
      setStep('code')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      onSuccess(await verifyLogin(email, code))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="login-form">
        <div className="login-form__card">
          <div className="login-form__logo">
            <img src={memoarLogo} alt="Memoar" className="login-form__logo-img" />
            <span className="login-form__logo-text">memoar</span>
          </div>

          {step === 'email' ? (
            <form onSubmit={submitEmail}>
              <h2>Sign in to Memoar</h2>
              <p>We'll email you a six-digit code.</p>
              <div className="form-group">
                <input
                  type="email" required autoFocus autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <button type="submit" className="primary-button" disabled={busy || !email}>
                {busy ? 'Sending…' : 'Send code'}
              </button>
            </form>
          ) : (
            <form onSubmit={submitCode}>
              <h2>Enter your code</h2>
              <p>We sent a code to {email}. It expires in 10 minutes.</p>
              <div className="form-group">
                <input
                  type="text" required autoFocus
                  className="otp-input"
                  inputMode="numeric" maxLength={6} autoComplete="one-time-code"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                />
              </div>
              <button type="submit" className="primary-button" disabled={busy || code.length !== 6}>
                {busy ? 'Verifying…' : 'Sign in'}
              </button>
              <button type="button" className="link-button" onClick={() => { setStep('email'); setCode('') }}>
                Use a different email
              </button>
              <p className="login-form__help">
                No code after a few minutes? Check your spam folder, or write to <SupportEmail />.
              </p>
            </form>
          )}
        </div>
      </div>

      <NotificationModal
        isOpen={error !== null}
        title="Sign-in failed"
        message={error ?? ''}
        variant="error"
        onClose={() => setError(null)}
      />
    </>
  )
}
